<template>
  <main class="min-h-screen bg-white" v-if="info">

    <template v-if="info.id == 1">
      <HeaderImage :title="info.title" :video="info.video" />
      <!-- Main Description -->
      <section class="py-12" ref="descriptionSection">
        <div class="grid md:grid-cols-2 gap-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="bg-gray-800 rounded-lg text-white p-10 place-content-center">
              <p class="text-2xl font-bold mb-2">{{ info.title4 }}</p>
              <p class="text-2xl text-justify italic mb-5" v-html="info.desc4"></p>
              <router-link to="/contact" class="bg-white text-black p-3 rounded-lg">Contact Us</router-link>
            </div>
            <div class="">
              <div class="prose-lg max-w-none" :class="{ 'animate-slideInUp': descriptionVisible }">
                <p class="text-lg leading-relaxed mb-8 text-justify">
                  {{ info.desc }}
                </p>

                <p class="text-xl font-bold mb-2">{{ info.title2 }}</p>
                <p class="text-lg leading-relaxed mb-8 text-justify" v-html="info.desc2"></p>

                <p class="text-xl font-bold mb-2">{{ info.title3 }}</p>
                <p class="text-lg leading-relaxed mb-8 text-justify" v-html="info.desc3"></p>
              </div>
            </div>
        </div>
      </section>
    </template>

    <!-- Key Features -->
    <template v-if="info.id == 2">
      <HeaderImage :title="info.title" :image="info.image" :desc="info.desc" />
      <section class="py-10" ref="featuresSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <!-- pain point grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            <div
              v-for="(item, index) in info.keyPainPoints"
              :key="'pain'+index"
              class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-red-200 transition-all duration-300" :class="{ 'animate-slideInUp': featuresVisible }" :style="featuresVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}"
            >
              <div class="flex items-start gap-4">
                <p class="text-xl font-bold">{{ index+1 }}</p>
                <h3 class="text-xl font-bold mb-2">{{ item.title }}</h3>
              </div>
              <p class="text-lg">{{ item.desc }}</p>
            </div>
          </div>

          <!-- introduction text from JSON -->
          <p class="text-2xl font-bold leading-relaxed mb-4 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" v-html="info.painPointsIntro"></p>
          <p class="text-lg leading-relaxed mb-8 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" v-html="info.companyDescription"></p>

          <!-- partner agnostic section -->
          <section class="" ref="partnerSection">
            <p class="text-2xl font-bold leading-relaxed mb-4 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" v-html="info.partnerAgnostic.title"></p>
            <p class="text-lg leading-relaxed mb-2 text-justify" :class="{ 'animate-slideInUp': partnerVisible }" v-html="info.partnerAgnostic.description"></p>
            <!-- two-column layout: image left, pillars right -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <!-- large image column -->
              <div>
                <img :src="require(`@/${info.partnerAgnostic.image}`)" class="w-full h-auto rounded-lg" />
              </div>
              <!-- pillar cards in a nested 2x2 grid -->
              <div class="grid grid-cols-1 gap-6">
                <div
                  v-for="(pillar, index) in info.partnerAgnostic.pillars"
                  :key="'pillar-'+index"
                  class="flex items-center gap-4"
                >
                  <template v-if="index == 0 || index == 2">
                    <div class="relative bg-gray-900 text-white border border-gray-800 rounded-xl p-6 text-center hover:shadow-lg hover:border-gray-700 transition-all duration-300 w-1/2"
                    :class="{ 'animate-slideInUp': partnerVisible }"
                    :style="partnerVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}">
                      <!-- numbered overlay -->
                      <span class="text-4xl font-bold opacity-20">
                        {{ index + 1 }}
                      </span>

                      <h3 class="text-xl font-bold mb-2 relative">
                        {{ pillar.title }}
                      </h3>
                    </div>
                    <div class="w-1/2">
                      <p class="text-xl relative">{{ pillar.desc }}</p>
                    </div>
                  </template>
                  <template v-else>
                    <div class="w-1/2">
                      <p class="text-xl relative text-right">{{ pillar.desc }}</p>
                    </div>
                    <div class="relative bg-gray-900 text-white border border-gray-800 rounded-xl p-6 text-center hover:shadow-lg hover:border-gray-700 transition-all duration-300 w-1/2"
                    :class="{ 'animate-slideInUp': partnerVisible }"
                    :style="partnerVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}">
                      <!-- numbered overlay -->
                      <span class="text-4xl font-bold opacity-20">
                        {{ index + 1 }}
                      </span>

                      <h3 class="text-xl font-bold mb-2 relative">
                        {{ pillar.title }}
                      </h3>
                    </div>
                  </template>
                </div>
                <router-link to="/contact" class="bg-gray-900 text-white p-5 rounded-lg w-1/2 text-center text-lg">Contact Us</router-link>
              </div>
            </div>
          </section>

          <!-- ai/dc cards -->
          <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-6 mt-10">
            <div 
              v-for="(item, index) in info.list" 
              :key="'item'+index"
              class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-red-200 transition-all duration-300" 
              :class="{ 'animate-slideInUp': featuresVisible }" 
              :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.3) + 's' } : {}"
            >
              <div class="relative">
                <img :src="require(`@/assets/t${index+1}.png`)" class="w-full h-48 object-cover" />
                <div class="absolute bottom-0 left-0 w-full bg-opacity-50 p-4">
                  <h3 class="text-white text-lg sm:text-xl font-bold">{{ item.title }}</h3>
                </div>
              </div>
              <p class="p-4 text-sm sm:text-base text-left">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Key Features -->
    <template v-if="info.id == 3">
      <HeaderImage :title="info.title" :video="info.video" />
      <section class="py-10" ref="featuresSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p class="text-xl font-bold mb-2" :class="{ 'animate-slideInUp': featuresVisible }">{{ info.title2 }}</p>
          <p class="text-lg leading-relaxed mb-4 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s" v-html="info.desc2"></p>
          <p class="text-lg mb-4" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s" v-html="info.title3"></p>

          <!-- dd -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div 
              v-for="(item, index) in info.list" 
              :key="'item'+index"
              class="relative group overflow-hidden rounded-xl transition-all duration-300" 
              :class="{ 'animate-slideInUp': featuresVisible, 'sm:top-[100px]': index==0, 'hidden sm:block': !item.title }" 
              :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.2) + 's' } : {}"
            >
              <template v-if="item.title">
                <!-- image with overlay text -->
                <img :src="require(`@/${item.image}`)" class="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500" />
                <div class="absolute inset-0 bg-black bg-opacity-20 flex flex-col justify-center items-center px-4 text-center">
                  <h3 class="text-white text-lg sm:text-xl font-bold mb-2">{{ item.title }}</h3>
                  <p class="text-white text-sm sm:text-base">{{ item.desc }}</p>
                </div>
                <!-- contact button bar at bottom -->
                <router-link to="/contact" class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white py-2 text-center text-sm font-semibold">
                  Contact Us
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </section>

      <!-- Lifecycle & Approach -->
      <section class="py-16 bg-gray-50" ref="lifecycleSection" v-if="false">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="lg:grid lg:grid-cols-3 lg:gap-12">
            <div v-for="(item, index) in info.list2" :key="'item--'+index" :class="{ 'animate-slideInUp': lifecycleVisible }" :style="lifecycleVisible ? { 'animation-delay': (index * 0.15) + 's' } : {}">
              <h2 class="text-lg font-bold mb-2">
                {{ item.title }}
              </h2>
              <ul class="space-y-2">
                <li v-for="stage in item.list" :key="stage" class="flex items-start">
                  <svg class="flex-shrink-0 w-6 h-6 text-red-600 mt-1.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m2.707 14.293 5.586-5.586a1 1 0 0 0 0-1.414L2.707 1.707A1 1 0 0 0 1 2.414v11.172a1 1 0 0 0 1.707.707Z"></path>
                  </svg>
                  <span class="text-lg">{{ stage }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- info 4 -->
    <section class="py-10" v-if="info.id == 4" ref="descriptionSection">
      <HeaderImage :title="info.title" :image="info.image" />
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- two-column layout: image left, text right -->
        <div class="grid md:grid-cols-2 gap-8 items-center mt-10">
          <div class="relative">
            <img :src="require(`@/assets/sol4_1.png`)" class="w-full h-auto rounded-lg shadow-lg" />
            <div class="absolute bottom-0 right-0 p-4 rounded-tl-lg">
              <p class="text-xl font-bold text-white">{{ info.title2 }}</p>
            </div>
          </div>
          <div class="prose-lg max-w-none border-y-4 border-blue-900 py-5" :class="{ 'animate-slideInUp': descriptionVisible }">
            <!-- title2 moved to overlay on image -->
            <p class="text-lg leading-relaxed mb-8 text-justify" v-html="info.desc2"></p>

            <router-link to="/contact" class="bg-blue-900 text-white p-3 px-10 rounded-lg inline-block">Contact Us</router-link>
          </div>
          <div class="prose-lg max-w-none border-y-4 border-blue-900 py-5" :class="{ 'animate-slideInUp': descriptionVisible }">
            <!-- title2 moved to overlay on image -->
            <p class="text-lg leading-relaxed mb-8 text-justify" v-html="info.desc3"></p>

            <div class="text-right">
              <router-link to="/contact" class="bg-blue-900 text-white p-3 px-10 rounded-lg inline-block">Contact Us</router-link>
            </div>
          </div>
          <div class="relative">
            <img :src="require(`@/assets/sol4_2.png`)" class="w-full h-auto rounded-lg shadow-lg" />
            <div class="absolute bottom-0 left-0 p-4 rounded-tl-lg">
              <p class="text-xl font-bold">{{ info.title3 }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

  </main>
</template>

<script>
import homeData from '@/data/home.json'
import HeaderImage from '@/components/HeaderImage.vue'

export default {
  name: 'ServicePage',
  components: {
    HeaderImage
  },
  data() {
    return {
      data: homeData,
      info: null,
      heroVisible: false,
      descriptionVisible: false,
      featuresVisible: false,
      techVisible: false,
      lifecycleVisible: false,
      detailsVisible: false,
      partnerVisible: false,
      sectionObserver: null,
    }
  },
  mounted() {
    const slug = this.$route?.params?.slug
    this.info = this.data.solutions.list.find(item => item.title.replace(/\s+/g, '-').toLowerCase() === slug) || null
    // Show hero immediately
    this.heroVisible = true
    // Set up observer for other sections
    this.$nextTick(() => {
      this.setupSectionObserver()
    })
  },
  beforeUnmount() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect()
    }
  },
  methods: {
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.heroVisible = true
        this.descriptionVisible = true
        this.featuresVisible = true
        this.techVisible = true
        this.lifecycleVisible = true
        this.detailsVisible = true
        this.partnerVisible = true
        return
      }
      
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const ref = entry.target.getAttribute('data-section')
              if (ref === 'hero') this.heroVisible = true
              if (ref === 'description') this.descriptionVisible = true
              if (ref === 'features') this.featuresVisible = true
              if (ref === 'tech') this.techVisible = true
              if (ref === 'lifecycle') this.lifecycleVisible = true
              if (ref === 'details') this.detailsVisible = true
              if (ref === 'partner') this.partnerVisible = true
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      
      if (this.$refs.heroSection) {
        this.$refs.heroSection.setAttribute('data-section', 'hero')
        this.sectionObserver.observe(this.$refs.heroSection)
      }
      if (this.$refs.descriptionSection) {
        this.$refs.descriptionSection.setAttribute('data-section', 'description')
        this.sectionObserver.observe(this.$refs.descriptionSection)
      }
      if (this.$refs.featuresSection) {
        this.$refs.featuresSection.setAttribute('data-section', 'features')
        this.sectionObserver.observe(this.$refs.featuresSection)
      }
      if (this.$refs.techSection) {
        this.$refs.techSection.setAttribute('data-section', 'tech')
        this.sectionObserver.observe(this.$refs.techSection)
      }
      if (this.$refs.partnerSection) {
        this.$refs.partnerSection.setAttribute('data-section', 'partner')
        this.sectionObserver.observe(this.$refs.partnerSection)
      }
      if (this.$refs.lifecycleSection) {
        this.$refs.lifecycleSection.setAttribute('data-section', 'lifecycle')
        this.sectionObserver.observe(this.$refs.lifecycleSection)
      }
      if (this.$refs.detailsSection) {
        this.$refs.detailsSection.setAttribute('data-section', 'details')
        this.sectionObserver.observe(this.$refs.detailsSection)
      }
    }
  }
}
</script>

<style scoped>
.prose {
  color: #374151;
}
.prose p {
  margin-bottom: 2rem;
}

/* Fade In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-in-out both;
}

/* Fade In Up Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}

/* Slide In Up Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInUp {
  animation: slideInUp 0.8s ease-out both;
}
</style>